import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { isMissing, isPresent, formatRate, difference } from '../utils/format';

const ARMRates = ({ onBackToHome }) => {
  const [selectedARM, setSelectedARM] = useState('5/1');
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Structural definition of each ARM product: how long the fixed period runs
  // and how often it adjusts afterwards. No rates here - `currentRate` used to
  // sit alongside these as 5.625 / 5.875 / 6.125 and was displayed as the live
  // quote for the product. Rates come from the API via marketData.armRates.
  const armProducts = {
    '5/1': {
      name: '5/1 ARM',
      description: 'Fixed for 5 years, then adjusts annually',
      fixedPeriod: 5,
      adjustmentFrequency: 1,
      index: 'SOFR',
      icon: '📊',
      rateKey: 'arm_5_1',
    },
    '7/1': {
      name: '7/1 ARM',
      description: 'Fixed for 7 years, then adjusts annually',
      fixedPeriod: 7,
      adjustmentFrequency: 1,
      index: 'SOFR',
      icon: '📈',
      rateKey: 'arm_7_1',
    },
    '10/1': {
      name: '10/1 ARM',
      description: 'Fixed for 10 years, then adjusts annually',
      fixedPeriod: 10,
      adjustmentFrequency: 1,
      index: 'SOFR',
      icon: '📉',
      rateKey: 'arm_10_1',
    }
  };

  // Terms used by the projection model below. These are assumptions about a
  // typical loan, not market readings, and the UI labels them as such - they
  // are not quotes and will differ by lender and borrower.
  const MODEL_TERMS = {
    margin: 2.75,
    initialCap: 2.0,
    periodicCap: 2.0,
    lifetimeCap: 5.0,
    // SOFR typically prints slightly below the fed funds rate.
    sofrDiscountToFedFunds: 0.10,
  };

  const fetchMarketData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getComprehensiveMarketData();

      if (response && response.data) {
        const d = response.data;

        // Every field is whatever the API returned, including null. The old
        // version supplied stand-ins (SOFR 5.32, fed funds 5.25, 30yr 6.875)
        // and a five-year Fed projection table written out by hand, so the page
        // rendered a full ARM analysis with no live data behind it.
        setMarketData({
          sofr: isPresent(d.sofr_rate) ? d.sofr_rate : d.fed_funds_rate ?? null,
          sofrTrend: d.sofr_trend ?? null,
          fedFunds: d.fed_funds_rate ?? null,
          // Requires a published FOMC projection feed; none is wired up.
          fedProjections: Array.isArray(d.fed_projections) ? d.fed_projections : null,
          fixed30: d.mortgage_rate_30y ?? null,
          armRates: {
            arm_5_1: d.arm_5_1 ?? null,
            arm_7_1: d.arm_7_1 ?? null,
            arm_10_1: d.arm_10_1 ?? null,
          },
        });
      } else {
        throw new Error('Invalid response format from market data API');
      }
    } catch (err) {
      console.error('Failed to fetch ARM market data:', err);
      setError(`Failed to load ARM rates data: ${err.message}. Please try refreshing the page.`);
      setMarketData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketData();
  }, []);

  const getCurrentARM = () => armProducts[selectedARM];

  /** Live quote for the selected ARM, or null when the API has no rate for it. */
  const getCurrentRate = () => {
    const value = marketData?.armRates?.[getCurrentARM().rateKey];
    return isMissing(value) ? null : Number(value);
  };

  /**
   * Projected rate at each future adjustment.
   *
   * Returns an empty list unless both a live starting rate and a published Fed
   * projection path are available - previously it ran off a hand-written
   * 2025-2029 rate path and a hardcoded starting rate, so it always produced a
   * confident 30-year projection regardless of what the API returned.
   */
  const calculateProjectedRates = () => {
    const arm = getCurrentARM();
    const currentRate = getCurrentRate();
    const fedProjections = marketData?.fedProjections;

    if (currentRate === null || !Array.isArray(fedProjections) || fedProjections.length === 0) {
      return [];
    }

    const projections = [];

    for (let year = 1; year <= arm.fixedPeriod; year++) {
      projections.push({ year, rate: currentRate, type: 'fixed' });
    }

    fedProjections.forEach((fedProj, index) => {
      if (isMissing(fedProj?.rate)) return;
      if (arm.fixedPeriod + index + 1 > 30) return;

      const estimatedSOFR = Number(fedProj.rate) - MODEL_TERMS.sofrDiscountToFedFunds;
      let newRate = estimatedSOFR + MODEL_TERMS.margin;

      if (index === 0) {
        newRate = Math.min(newRate, currentRate + MODEL_TERMS.initialCap);
      } else {
        const previousRate = projections[projections.length - 1]?.rate ?? currentRate;
        newRate = Math.min(
          Math.max(newRate, previousRate - MODEL_TERMS.periodicCap),
          previousRate + MODEL_TERMS.periodicCap
        );
      }

      newRate = Math.min(newRate, currentRate + MODEL_TERMS.lifetimeCap);

      projections.push({
        year: arm.fixedPeriod + index + 1,
        rate: Math.max(newRate, 0),
        type: 'adjustable',
        index: estimatedSOFR,
      });
    });

    return projections;
  };

  /** Initial saving vs the 30-year fixed, or null if either leg is unavailable. */
  const getSavingsVsFixed = () => difference(marketData?.fixed30, getCurrentRate());

  const Unavailable = () => <span className="text-gray-400 font-normal">Not available</span>;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={onBackToHome}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              <span>←</span>
              <span>Back to Home</span>
            </button>
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900">Adjustable Rate Mortgages (ARMs)</h1>
              <p className="text-lg text-gray-600 mt-2">
                ARM rates, trends, and adjustment projections
              </p>
            </div>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* ARM Type Selector */}
        <div className="mb-8">
          <div className="flex justify-center space-x-4">
            {Object.entries(armProducts).map(([key, arm]) => (
              <button
                key={key}
                onClick={() => setSelectedARM(key)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  selectedARM === key
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <span className="text-xl mr-2">{arm.icon}</span>
                {arm.name}
              </button>
            ))}
          </div>
        </div>

        {/* Current Savings Alert - only when both legs of the comparison exist */}
        {!loading && marketData && isPresent(getSavingsVsFixed()) && (
          <div className="mb-8 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">💰</span>
                <div>
                  <h3 className="font-semibold text-green-900">Current ARM Advantage</h3>
                  <p className="text-sm text-green-700">
                    {getCurrentARM().name} vs. 30-Year Fixed Rate
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-600">
                  -{formatRate(getSavingsVsFixed(), 3)}
                </div>
                <div className="text-sm text-green-600">initial savings</div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* ARM Details */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center space-x-3 mb-6">
                <span className="text-3xl">{getCurrentARM().icon}</span>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{getCurrentARM().name}</h2>
                  <p className="text-gray-600">{getCurrentARM().description}</p>
                </div>
              </div>

              {/* Key Terms Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-sm text-blue-600">Current Rate</div>
                  <div className="text-lg font-bold text-blue-800">
                    {isPresent(getCurrentRate()) ? formatRate(getCurrentRate(), 3) : <Unavailable />}
                  </div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Margin</div>
                  <div className="text-lg font-bold text-gray-900">{formatRate(MODEL_TERMS.margin, 3)}</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Initial Cap</div>
                  <div className="text-lg font-bold text-gray-900">{formatRate(MODEL_TERMS.initialCap, 3)}</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Lifetime Cap</div>
                  <div className="text-lg font-bold text-gray-900">{formatRate(MODEL_TERMS.lifetimeCap, 3)}</div>
                </div>
              </div>

              {/* Cap Structure Explanation */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 mb-2">📋 How Rate Adjustments Work</h4>
                <div className="text-sm text-yellow-700 space-y-1">
                  <p><strong>Index:</strong> {getCurrentARM().index} (currently {formatRate(marketData?.sofr, 3)})</p>
                  <p><strong>Margin:</strong> {formatRate(MODEL_TERMS.margin, 3)} (added to index at each adjustment)</p>
                  <p><strong>Initial Cap:</strong> Rate can't increase more than {formatRate(MODEL_TERMS.initialCap, 3)} at first adjustment</p>
                  <p><strong>Periodic Cap:</strong> Rate can't change more than {formatRate(MODEL_TERMS.periodicCap, 3)} per year thereafter</p>
                  <p>
                    <strong>Lifetime Cap:</strong>{' '}
                    {isPresent(getCurrentRate())
                      ? `Rate can never exceed ${formatRate(getCurrentRate() + MODEL_TERMS.lifetimeCap, 3)}`
                      : `Rate can never exceed the starting rate plus ${formatRate(MODEL_TERMS.lifetimeCap, 3)}`}
                  </p>
                </div>
                <p className="text-xs text-yellow-800 mt-3 pt-2 border-t border-yellow-200">
                  Margin and caps are illustrative terms for a typical loan, not quotes. Your lender's
                  figures will differ.
                </p>
              </div>
            </div>

            {/* Rate Projection Chart */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <span className="mr-2">📈</span>
                Projected Rate Path
              </h3>
              
              {!loading && marketData && calculateProjectedRates().length === 0 && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                  A rate path needs both a live starting rate for this product and a published Fed
                  projection path. At least one of those is unavailable, so no projection is shown.
                </div>
              )}

              {!loading && marketData && calculateProjectedRates().length > 0 && (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 mb-4">
                    Based on current Fed projections and {getCurrentARM().index} trends
                  </div>
                  
                  {/* Rate projection timeline */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {calculateProjectedRates().slice(0, 12).map((projection, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg border ${
                          projection.type === 'fixed' 
                            ? 'bg-blue-50 border-blue-200' 
                            : 'bg-yellow-50 border-yellow-200'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Year {projection.year}</span>
                          <span className="font-bold">{formatRate(projection.rate, 3)}</span>
                        </div>
                        <div className="text-xs mt-1">
                          {projection.type === 'fixed' ? (
                            <span className="text-blue-600">Fixed Period</span>
                          ) : (
                            <span className="text-yellow-600">
                              {getCurrentARM().index}: {formatRate(projection.index, 3)} + Margin
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2">Projection Assumptions</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Based on current Fed dot plot projections</li>
                      <li>• Assumes {getCurrentARM().index} tracks Fed Funds Rate trends</li>
                      <li>• Subject to caps and adjustment frequency limits</li>
                      <li>• Actual rates may vary based on market conditions</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* ARM vs Fixed Comparison */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">⚖️</span>
                ARM vs Fixed Comparison
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium text-blue-800">{getCurrentARM().name}</span>
                  <span className="font-bold text-blue-800">{isPresent(getCurrentRate()) ? formatRate(getCurrentRate(), 3) : <Unavailable />}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-600">30-Year Fixed</span>
                  <span className="font-bold text-gray-800">{formatRate(marketData?.fixed30, 3)}</span>
                </div>
                {isPresent(getSavingsVsFixed()) && (
                  <div className="text-center pt-2">
                    <div className="text-lg font-bold text-green-600">
                      Save {formatRate(getSavingsVsFixed(), 3)} initially
                    </div>
                    <div className="text-xs text-gray-500">
                      During {getCurrentARM().fixedPeriod}-year fixed period
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Market Context */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📊</span>
                Current Market Context
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">SOFR (ARM Index)</span>
                  <span className="font-medium">{formatRate(marketData?.sofr, 3)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Fed Funds Rate</span>
                  <span className="font-medium">{formatRate(marketData?.fedFunds, 3)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">SOFR Trend</span>
                  <span className="font-medium">
                    {marketData?.sofrTrend === 'stable' && '➡️ Stable'}
                    {marketData?.sofrTrend === 'rising' && '📈 Rising'}
                    {marketData?.sofrTrend === 'falling' && '📉 Falling'}
                  </span>
                </div>
              </div>
            </div>

            {/* Risk Assessment */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">⚠️</span>
                ARM Risk Factors
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start space-x-2">
                  <span className="text-orange-600 mt-1">•</span>
                  <span className="text-gray-700">
                    <strong>Rate Risk:</strong> Payments can increase significantly after fixed period
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-orange-600 mt-1">•</span>
                  <span className="text-gray-700">
                    <strong>Payment Shock:</strong> Budget for potential payment increases
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-orange-600 mt-1">•</span>
                  <span className="text-gray-700">
                    <strong>Timing Risk:</strong> May need to refinance before rates rise
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-green-600 mt-1">•</span>
                  <span className="text-gray-700">
                    <strong>Protection:</strong> Caps limit how much rates can increase
                  </span>
                </div>
              </div>
            </div>

            {/* When ARMs Make Sense */}
            <div className="bg-green-50 rounded-lg border border-green-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">✅</span>
                When ARMs Make Sense
              </h3>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex items-center space-x-2">
                  <span className="text-green-600">✓</span>
                  <span>Planning to move/refinance within {getCurrentARM().fixedPeriod} years</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-600">✓</span>
                  <span>Expect income to increase over time</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-600">✓</span>
                  <span>Comfortable with payment variability</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-600">✓</span>
                  <span>Want lower initial payments</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-600">✓</span>
                  <span>Believe rates will decline or stay stable</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ARMRates;
